<template>
  <!-- The CI page of "webmag Editor v3" as a brand kit (spec 2026-09-30):
       tiles with small samples on the left, a live page on the right. A tile
       opens only its editor; the editors come from the parent as named slots
       (`<tile>` and `<tile>-details`), so every write path stays there. All
       panels stay mounted (v-show, Rev. 2 #1). -->
  <div class="ci-kit" data-test="ci-brand-kit">
    <div class="ci-kit-zeile">
      <!-- "CI von Webseite" (spec 2026-10-03 §9): while the parent fills slot `import`, it takes the place of
           tiles, editors and preview; it brings its own preview. -->
      <div class="ci-kit-links" :class="{ 'ci-kit-links-voll': !!$scopedSlots.import }">
        <div class="ci-kit-kopf">
          <div>
            <h3 class="ci-kit-titel">{{ t('titel') }}</h3>
            <div class="ci-kit-unter">{{ t('untertitel') }}</div>
          </div>
          <!-- "Katalog in deiner CI" (spec 2026-10-03 Lesbarkeit §7), from the parent. -->
          <slot name="aktion" />
        </div>

        <slot v-if="$scopedSlots.import" name="import" />
        <!-- v-show: the tile editors stay mounted while the import is open (Rev. 2 #1). -->
        <div v-show="!$scopedSlots.import" class="ci-kit-inhalt">
        <!-- After an import: what came in, with undo (from the parent). -->
        <slot name="band" />

        <div v-show="!offen" class="ci-kit-raster" data-test="ci-kit-raster">
          <button
            v-for="k in kacheln"
            :key="k"
            :ref="`kachel-${k}`"
            type="button"
            class="ci-kachel"
            :data-test="`ci-kachel-${k}`"
            @click="oeffne(k)"
          >
            <span class="ci-klein">{{ t(`kachel.${k}`) }}</span>
            <!-- Light chip: a dark logo stays readable on the dark tile. -->
            <span v-if="k === 'logo'" class="ci-muster ci-m-logo-chip">
              <img v-if="bild('favicon')" :src="bild('favicon')" alt="" class="ci-m-favicon">
              <span v-else class="ci-m-favicon" :style="{ background: farben.primary }">W</span>
              <img v-if="bild('logo')" :src="bild('logo')" alt="" class="ci-m-logo">
              <span v-else class="ci-m-logo ci-m-logo-leer">{{ t('logoPlatzhalter') }}</span>
            </span>
            <span v-else-if="k === 'farben'" class="ci-muster">
              <span class="ci-m-chip" :style="{ background: farben.primary }" />
              <span v-if="farben.secondary" class="ci-m-chip" :style="{ background: farben.secondary }" />
              <span class="ci-m-trenner" />
              <span v-for="g in ['ink', 'surface', 'page']" :key="g" class="ci-m-chip ci-m-chip-klein" :style="{ background: farben[g] }" />
            </span>
            <span v-else-if="k === 'schriften'" class="ci-muster ci-muster-grund">
              <span class="ci-m-aa" :style="{ fontFamily: schrift('h1') }">Aa</span>
              <span class="ci-m-aa ci-m-aa-text" :style="{ fontFamily: schrift('body') }">Aa</span>
              <span class="ci-m-namen">{{ schriftNamen }}</span>
            </span>
            <span v-else-if="k === 'typo'" class="ci-muster ci-muster-spalte">
              <span class="ci-m-titel" :style="typoTitelStil">{{ t('typoTitel') }}</span>
              <span class="ci-m-text" :style="{ fontFamily: schrift('body') }">{{ $t('settings.ciBrandKit.typoText', { px: fliesstextPx }) }}</span>
            </span>
            <span v-else-if="k === 'formen'" class="ci-muster">
              <span class="ci-m-karte" :style="{ borderRadius: klein(tok.radius.card), boxShadow: tok.shadowCard }" />
              <span class="ci-m-bild" :style="{ borderRadius: klein(tok.radius.image) }" />
              <span class="ci-m-box" :style="{ borderRadius: klein(tok.radius.box) }" />
            </span>
            <span v-else-if="k === 'abstand'" class="ci-muster" :style="{ gap: tok.space.l }">
              <span v-for="i in 3" :key="i" class="ci-m-klotz" />
            </span>
            <span v-else-if="k === 'knopf'" class="ci-muster">
              <span class="ci-m-knopf" :style="knopfStil">{{ t('vKnopf') }}</span>
            </span>
            <span v-else class="ci-m-mehr">{{ t('mehrText') }}</span>
          </button>
        </div>

        <div
          v-for="k in kacheln"
          v-show="offen === k"
          :key="`panel-${k}`"
          class="ci-kit-editor"
          :data-test="`ci-panel-${k}`"
        >
          <div class="ci-kit-editor-kopf">
            <h4 class="ci-kit-editor-titel">{{ t(`kachel.${k}`) }}</h4>
            <button :ref="`zurueck-${k}`" type="button" class="ci-link" :data-test="`ci-zurueck-${k}`" @click="zurueck">
              {{ t('zurueck') }}
            </button>
          </div>
          <slot :name="k" />
          <template v-if="mitDetails.includes(k)">
            <button
              type="button"
              class="ci-details-knopf"
              :class="{ offen: details[k] }"
              :aria-expanded="String(!!details[k])"
              :data-test="`ci-details-${k}`"
              @click="$set(details, k, !details[k])"
            ><span>{{ t(details[k] ? 'weniger' : 'details') }}</span><span class="ci-pfeil" aria-hidden="true" /></button>
            <div v-show="details[k]" class="ci-kit-details" :data-test="`ci-details-panel-${k}`">
              <slot :name="`${k}-details`" />
            </div>
          </template>
        </div>
        </div>
      </div>

      <div v-if="!$scopedSlots.import" class="ci-kit-vorschau">
        <div class="ci-klein ci-kit-vorschau-titel">{{ t(offen === 'typo' ? 'vorschauTypo' : 'vorschau') }}</div>
        <ci-brand-vorschau :ci="ci" :aktive-rolle="offen === 'typo' ? rolle : null" @waehle="ausVorschau" />
      </div>
    </div>
  </div>
</template>

<script>
import { resolveDesignTokenPreview } from '@/editor-v2/utils/ciTokens';
import { vorschauStil } from '@/components/settings/typoTabelle';
import { wirksameFarben } from './ciFarbenV25';
import CiBrandVorschau from './CiBrandVorschau.vue';

// Order as in the prototype the founder chose (board A).
export const KACHELN = Object.freeze(['logo', 'farben', 'schriften', 'typo', 'formen', 'abstand', 'knopf', 'mehr']);
// Tiles with a "Details: genaue Werte" part (slot `<tile>-details`).
export const MIT_DETAILS = Object.freeze(['farben', 'schriften', 'formen', 'abstand', 'knopf']);

export default {
  name: 'CiBrandKit',
  components: { CiBrandVorschau },
  props: {
    // corporate_identity, read only: the tiles show samples of it.
    ci: { type: Object, required: true },
    // Role of the typography tile (.sync; the parent binds the same to TypoVorschau).
    rolle: { type: String, default: 'h1' },
  },
  data: () => ({
    offen: null,
    details: {},
    kacheln: KACHELN,
    mitDetails: MIT_DETAILS,
  }),
  computed: {
    farben() {
      return wirksameFarben(this.ci);
    },
    tok() {
      return resolveDesignTokenPreview(this.ci.designTokens);
    },
    typo() {
      return (this.ci.designTokens && this.ci.designTokens.typography) || {};
    },
    schriftNamen() {
      const name = (n) => String(n || '').replace(/^'(.*)'$/, '$1');
      return [...new Set([name(this.typo.displayFont), name(this.typo.textFont)].filter(Boolean))].join(' · ');
    },
    typoTitelStil() {
      const st = vorschauStil('h1', this.typo, 'desktop');
      return { ...st, fontSize: `${Math.min(30, parseFloat(st.fontSize))}px`, lineHeight: '1.1' };
    },
    fliesstextPx() {
      return Math.round(parseFloat(vorschauStil('body', this.typo, 'desktop').fontSize));
    },
    knopfStil() {
      return {
        ...vorschauStil('button', this.typo, 'desktop'),
        fontSize: '15px',
        background: this.farben.primary,
        padding: this.tok.buttonPadding,
        borderRadius: this.tok.radius.button,
      };
    },
  },
  methods: {
    t(key) {
      return this.$t(`settings.ciBrandKit.${key}`);
    },
    bild(key) {
      const b = this.ci[key] && this.ci[key].image;
      return (b && b.url) || '';
    },
    schrift(role) {
      return vorschauStil(role, this.typo, 'desktop').fontFamily;
    },
    // Tile samples are small: a pill radius would turn them into circles.
    klein(radius) {
      const px = parseFloat(radius);
      return Number.isNaN(px) ? radius : `${Math.min(px, 21)}px`;
    },
    fokus(ref) {
      this.$nextTick(() => {
        const el = [].concat(this.$refs[ref] || [])[0];
        if (el && el.focus) el.focus();
      });
    },
    oeffne(k) {
      this.offen = k;
      this.fokus(`zurueck-${k}`);
    },
    // Back to the grid, focus on the tile that was open (Rev. 2 #11).
    zurueck() {
      const war = this.offen;
      this.offen = null;
      if (war) this.fokus(`kachel-${war}`);
    },
    // A click in the preview opens the matching tile (typography with its role).
    ausVorschau({ kachel, rolle }) {
      if (rolle) this.$emit('update:rolle', rolle);
      if (this.offen !== kachel) this.oeffne(kachel);
    },
  },
};
</script>

<style lang="scss" scoped>
/* Layout (Rev. 2 #10): width 0 + min-width keep the kit next to the tab list;
   -15px/15px take in the negative margins of the bootstrap rows inside. */
.ci-kit {
  width: 0; min-width: calc(100% + 30px); margin: 0 -15px; padding: 0 15px; box-sizing: border-box;
}
.ci-kit-zeile { display: flex; flex-wrap: wrap; gap: 32px; align-items: flex-start; max-width: none; }
.ci-kit-zeile > * { min-width: 0; }
.ci-kit-links { flex: 0 1 560px; display: flex; flex-direction: column; gap: 18px; }
.ci-kit-links-voll { flex: 1 1 100%; }
.ci-kit-inhalt { display: contents; }
.ci-kit-vorschau {
  flex: 1 1 420px; position: sticky; top: 80px; align-self: flex-start;
  display: flex; flex-direction: column; gap: 10px;
}

/* Calm surfaces (refactoring-ui, feedback 30.09.2026): hierarchy through
   fill and weight, no hard borders; one spacing scale (8/12/16/24/32). */
.ci-kit-kopf { display: flex; flex-wrap: wrap; gap: 12px 24px; align-items: flex-start; justify-content: space-between; }
.ci-kit-titel { margin: 0; color: #fff; }
.ci-kit-unter { font-size: 15px; color: #c9cbce; margin-top: 6px; }
.ci-klein { font-size: 12px; letter-spacing: 1px; font-weight: 700; color: #a9acb1; text-transform: uppercase; }

.ci-kit-raster { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.ci-kachel {
  all: unset; box-sizing: border-box; cursor: pointer; display: flex; flex-direction: column; gap: 16px;
  padding: 20px; min-height: 128px; min-width: 0; background: #44474d; border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2); transition: background 0.15s, box-shadow 0.15s, transform 0.15s;
}
.ci-kachel:hover {
  background: #4a4d54; transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.28), 0 0 0 1px rgba(127, 214, 164, 0.45);
}
.ci-kachel:focus-visible { outline: 2px solid #7fd6a4; outline-offset: 2px; }

.ci-muster { display: flex; align-items: center; gap: 12px; min-width: 0; }
.ci-muster-grund { align-items: baseline; }
.ci-muster-spalte { flex-direction: column; align-items: flex-start; gap: 4px; }
.ci-m-logo-chip { align-self: flex-start; max-width: 100%; box-sizing: border-box; padding: 8px 12px; background: #f4f4f2; border-radius: 8px; }
.ci-m-logo { max-width: 120px; height: 32px; object-fit: contain; min-width: 0; }
.ci-m-logo-leer {
  display: inline-flex; align-items: center; justify-content: center; width: 110px; box-sizing: border-box;
  border: 2px dashed #b9bbbf; border-radius: 6px; font-size: 12px; font-weight: 700; letter-spacing: 2px;
  color: #6a6d73; text-transform: uppercase;
}
.ci-m-favicon {
  width: 28px; height: 28px; border-radius: 6px; object-fit: contain; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center; color: #fff; font-weight: 700;
}
.ci-m-chip { width: 40px; height: 40px; border-radius: 8px; flex-shrink: 0; }
.ci-m-chip-klein { width: 28px; height: 28px; border-radius: 6px; box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.18); }
.ci-m-trenner { width: 1px; height: 28px; background: rgba(255, 255, 255, 0.14); margin: 0 4px; }
.ci-m-aa { font-size: 38px; font-weight: 700; color: #fff; line-height: 1; }
.ci-m-aa-text { font-size: 30px; font-weight: 400; color: #e9e9e7; }
.ci-m-namen { font-size: 13px; color: #a9acb1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci-m-titel { color: #fff; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci-m-text { font-size: 14px; color: #c9cbce; }
.ci-m-karte { width: 54px; height: 42px; background: #fff; }
.ci-m-bild { width: 42px; height: 42px; background: #c9c6bf; }
.ci-m-box { width: 42px; height: 42px; border: 2px solid #c9c6bf; box-sizing: border-box; }
.ci-m-klotz { width: 36px; height: 24px; background: #6a6d73; border-radius: 4px; }
.ci-m-knopf { display: inline-block; color: #fff; font-size: 15px; }
.ci-m-mehr { font-size: 14px; line-height: 1.45; color: #c9cbce; }

.ci-kit-editor {
  background: #44474d; border-radius: 12px; padding: 24px 28px 28px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2); display: flex; flex-direction: column; gap: 24px;
}
.ci-kit-editor-kopf { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.ci-kit-editor-titel { margin: 0; font-size: 20px; font-weight: 700; color: #fff; }
.ci-link { all: unset; cursor: pointer; color: #7fd6a4; font-size: 14px; font-weight: 600; }
.ci-link:hover { color: #a6e4bf; }
.ci-link:focus-visible { outline: 2px solid #7fd6a4; outline-offset: 2px; border-radius: 4px; }
/* The details toggle: full width, a hairline above, a chevron that turns. */
.ci-details-knopf {
  all: unset; box-sizing: border-box; cursor: pointer; align-self: stretch;
  display: flex; justify-content: space-between; align-items: center; gap: 12px;
  padding-top: 16px; border-top: 1px solid rgba(255, 255, 255, 0.08);
  color: #d9dadc; font-size: 14px; font-weight: 600;
}
.ci-details-knopf:hover { color: #fff; }
.ci-details-knopf:focus-visible { outline: 2px solid #7fd6a4; outline-offset: 4px; border-radius: 4px; }
.ci-pfeil {
  width: 8px; height: 8px; margin-right: 4px; border-right: 2px solid currentColor; border-bottom: 2px solid currentColor;
  transform: rotate(45deg); transition: transform 0.15s;
}
.ci-details-knopf.offen .ci-pfeil { transform: rotate(-135deg); }
/* Inset one step darker instead of a box. */
.ci-kit-details { margin-top: -8px; padding: 24px; background: #3d4045; border-radius: 10px; }

/* Card shadow colour choices and the narrow px fields (Paket 7). */
.ci-kit-editor ::v-deep .ci-kit-swatch {
  width: 28px; height: 28px; padding: 0; border: 2px solid #5b6064; border-radius: 50%; cursor: pointer;
}
.ci-kit-editor ::v-deep .ci-kit-swatch.aktiv { border-color: #7fd6a4; }
.ci-kit-editor ::v-deep .ci-kit-px { max-width: 240px; }
.ci-kit-editor ::v-deep .ci-kit-schatten-zahlen { display: flex; flex-wrap: wrap; gap: 8px; max-width: 420px; }
.ci-kit-editor ::v-deep .ci-kit-schatten-zahlen .input-group { flex: 1 1 120px; flex-wrap: nowrap; }
.ci-kit-editor ::v-deep .ci-kit-schatten-zahlen .form-control { min-width: 48px; }

/* Hint under a field (from the slots). */
.ci-kit-editor ::v-deep .ci-kit-hinweis { margin-top: 8px; font-size: 13px; line-height: 1.45; color: #a9acb1; }

/* Segmented control for every `b-form-radio-group buttons` in the kit
   (feedback 30.09.2026). !important: the admin styles every .tab-pane label
   (uppercase, 13px) with an id selector. Scoped to the kit editor. */
.ci-kit-editor ::v-deep .btn-group-toggle {
  display: inline-flex; flex-wrap: wrap; gap: 2px; padding: 3px; max-width: 100%;
  background: #36393d; border-radius: 10px;
}
.ci-kit-editor ::v-deep .btn-group-toggle > .btn {
  flex: 0 0 auto; display: inline-flex; align-items: center; height: 36px; margin: 0;
  padding: 0 14px; border: 0; border-radius: 8px !important; background: transparent; box-shadow: none;
  font-size: 14px !important; font-weight: 500 !important; letter-spacing: normal !important;
  text-transform: none !important; color: #d9dadc !important; line-height: 1;
}
.ci-kit-editor ::v-deep .btn-group-toggle > .btn:hover { background: rgba(255, 255, 255, 0.06); color: #fff !important; }
.ci-kit-editor ::v-deep .btn-group-toggle > .btn.active {
  background: #5b6064; color: #fff !important; font-weight: 700 !important; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}
.ci-kit-editor ::v-deep .btn-group-toggle > .btn.focus,
.ci-kit-editor ::v-deep .btn-group-toggle > .btn:focus-within { outline: 2px solid #7fd6a4; outline-offset: 1px; box-shadow: none; }

/* In the editor every bootstrap column takes the full width (label above field). */
.ci-kit-editor ::v-deep .row { max-width: none; margin-left: 0; margin-right: 0; }
.ci-kit-editor ::v-deep .row > [class*="col-"] { flex: 0 0 100%; max-width: 100%; margin-left: 0; padding-left: 0; padding-right: 0; }
</style>
